import { Box, List, ListItem, Tooltip, Typography } from "@mui/material";
import { Phrase } from "../../../api/phrasebook";
import { Circle, GTranslate } from "@mui/icons-material";
import { phraseColorByStatus } from "./colors";
import usePhrasebookStore from "./store";
import {
  LanguageCode,
  languageCodeToLabel,
} from "../../../pages/playground/types";
import { useAuthStore } from "../../../authProvider";
import FormattedText from "../../common/formatted-text";

export default function EditorPhrasebookSearchItems({
  phrases,
  loading,
  query,
  brandsWithGlobalPhrases,
  languagesToTranslate,
  onUsageCountClick,
  originalLanguage,
}: {
  phrases: Phrase[];
  loading: boolean;
  query: string;
  brandsWithGlobalPhrases: { id: string; name: string }[];
  languagesToTranslate: LanguageCode[];
  onUsageCountClick: (phrase: string) => void;
  originalLanguage?: string;
}) {
  const { user } = useAuthStore();
  const { jobId, setSelectedPhrase, garmentType } = usePhrasebookStore();

  /**
   * Calculate the ratio of available translations compared to required languages
   * @param phrase The phrase with translation status
   * @param requiredLanguages The list of languages that should be translated
   * @returns An object with count of available translations and total required
   */
  const getTranslationRatio = (
    phrase: Phrase,
    requiredLanguages: LanguageCode[]
  ) => {
    if (!phrase.translation_status || requiredLanguages.length === 0) {
      return { available: 0, total: 0 };
    }

    // Count available translations that are in the required languages list
    const availableCount = phrase.translation_status
      ? Object.entries(phrase.translation_status).filter(
          ([langCode, available]) =>
            Boolean(available) &&
            requiredLanguages.includes(langCode as LanguageCode)
        ).length
      : 0;

    return { available: availableCount, total: requiredLanguages.length };
  };

  /**
   * Determine the background color for the translation indicator based on completion ratio
   * @param phrase The phrase with translation status
   * @param requiredLanguages The list of languages that should be translated
   * @returns A color code for the translation indicator
   */
  const getTranslationColor = (
    phrase: Phrase,
    requiredLanguages: LanguageCode[]
  ) => {
    // If translation status is missing or no required languages, use a neutral color
    if (
      !phrase.translation_status ||
      !requiredLanguages ||
      requiredLanguages.length === 0
    ) {
      return "#d4edff"; // Very light blue
    }

    const { available, total } = getTranslationRatio(phrase, requiredLanguages);
    const ratio = total > 0 ? available / total : 0;

    // Color scale from red to yellow to green based on completion ratio - using even lighter shades
    if (ratio === 1) return "#c8e6c9"; // All translations available - very light green
    if (ratio >= 0.7) return "#dcedc8"; // Most translations available - even lighter green
    if (ratio >= 0.5) return "#fff9c4"; // Half translations available - very light yellow
    if (ratio > 0) return "#ffe0b2"; // Some translations available - very light orange
    return "#ffcdd2"; // No translations available - very light red
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
      role="tabpanel"
      id="phrases-panel"
      aria-labelledby="phrases-tab"
    >
      {phrases.length === 0 && !loading && (
        <Box sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary" component="div">
            {query === ""
              ? "Please enter at least one character to start searching in the phrasebook."
              : <>No results in the phrasebook for the search: &quot;<FormattedText text={query} />&quot;{
                  originalLanguage
                    ? ` (language requested: ${languageCodeToLabel(
                        originalLanguage as LanguageCode
                      )})`
                    : ""
                }</>}
          </Typography>
        </Box>
      )}

      {phrases.length > 0 && (
        <List sx={{ flex: 1, p: 0 }}>
          {phrases.map((phrase, index) => {
            const brandNames = phrase.brand_ids?.map(
              (id) =>
                brandsWithGlobalPhrases?.find((brand) => brand.id === id)?.name
            );

            // Truncate brand names if too many
            const displayBrandNames =
              brandNames && brandNames.length > 0
                ? brandNames.length > 1
                  ? `${brandNames[0]} +${brandNames.length - 1}`
                  : brandNames[0]
                : null;

            let usageCount = phrase.usage_count;

            if (
              phrase?.job_ids &&
              phrase?.job_ids?.length > 0 &&
              phrase.job_ids?.includes(jobId) &&
              usageCount
            ) {
              usageCount -= 1;
            }

            const missingRequiredTranslations =
              (languagesToTranslate &&
                languagesToTranslate
                  .filter(
                    (langCode) =>
                      phrase.translation_status &&
                      !phrase.translation_status[langCode] &&
                      phrase.translation_status[langCode] !== undefined
                  )
                  .map((langCode) => languageCodeToLabel(langCode))) ||
              [];

            return (
              <ListItem
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  backgroundColor: index % 2 === 1 ? "#f2f2f2" : "",
                  cursor: "pointer",
                  color: loading ? "text.disabled" : "text.primary",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  py: 1,
                  px: 2,
                }}
                key={phrase.id}
                onClick={() => {
                  setSelectedPhrase(phrase);
                }}
              >
                <Box
                  sx={{
                    mr: 1,
                    display: user?.isClientRelated ? "none" : "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {usageCount && usageCount > 0 ? (
                    <Tooltip
                      title={`This phrase has been reused ${usageCount} times within the batch. Click to view specific SKUs.`}
                    >
                      <Typography
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUsageCountClick(phrase.phrase);
                        }}
                        sx={{
                          backgroundColor:
                            phraseColorByStatus[phrase?.status || "empty"],
                          color: "info.contrastText",
                          fontSize: "0.7rem",
                          minWidth: "16px",
                          height: "16px",
                          py: 0.1,
                          px: 0.4,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          lineHeight: 1,
                        }}
                      >
                        {usageCount}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Circle
                      sx={{
                        fontSize: "0.75rem",
                        color: loading
                          ? "text.disabled"
                          : phraseColorByStatus[phrase?.status || "empty"],
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    minWidth: "200px",
                    mr: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: phrase.is_matching_garment_type ? 500 : 400,
                      display: "block",
                      wordBreak: "break-word",
                    }}
                  >
                    <FormattedText text={phrase.phrase} />
                  </Typography>
                </Box>

                {user?.isClientRelated && (
                  <Box
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      ml: 1,
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {/* Brand/batch indicator */}
                    {displayBrandNames && (
                      <Tooltip title={brandNames?.join(", ") || ""}>
                        <Box
                          component="span"
                          sx={{
                            backgroundColor: "grey.200",
                            color: "grey.700",
                            fontSize: "0.7rem",
                            py: 0.3,
                            px: 0.6,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                          }}
                        >
                          {displayBrandNames}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    display: user?.isClientRelated ? "none" : "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    ml: 1,
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  {/* Translation indicator */}
                  {phrase.translation_status &&
                    languagesToTranslate &&
                    languagesToTranslate.length > 0 && (
                      <Tooltip
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "#3b3b3b",
                              color: "#ffffff",
                              "& .MuiTooltip-arrow": {
                                color: "#3b3b3b",
                              },
                            },
                          },
                        }}
                        placement="left"
                        title={
                          <Box
                            sx={{
                              p: 1,
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: "bold", color: "white" }}
                            >
                              Translations Available:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, p: 0, pl: 2 }}>
                              {/* First show required languages that are available */}
                              {languagesToTranslate &&
                              languagesToTranslate.length > 0
                                ? Object.entries(phrase.translation_status)
                                    .filter(
                                      ([langCode, available]) =>
                                        Boolean(available) &&
                                        languagesToTranslate.includes(
                                          langCode as LanguageCode
                                        )
                                    )
                                    .map(([langCode]) => (
                                      <Typography
                                        component="li"
                                        variant="caption"
                                        key={`req-${langCode}`}
                                        sx={{
                                          color: "#79c1fc",
                                          fontWeight: "medium",
                                        }}
                                      >
                                        {languageCodeToLabel(
                                          langCode as LanguageCode
                                        )}
                                      </Typography>
                                    ))
                                : null}

                              {phrase.translation_status &&
                                Object.entries(phrase.translation_status)
                                  .filter(
                                    ([langCode, available]) =>
                                      Boolean(available) &&
                                      (!languagesToTranslate ||
                                        !languagesToTranslate.includes(
                                          langCode as LanguageCode
                                        ))
                                  )
                                  .map(([langCode]) => (
                                    <Typography
                                      component="li"
                                      variant="caption"
                                      key={`nonreq-${langCode}`}
                                      sx={{
                                        color: "white",
                                        opacity: 0.7,
                                      }}
                                    >
                                      {languageCodeToLabel(
                                        langCode as LanguageCode
                                      )}
                                    </Typography>
                                  ))}
                            </Box>

                            {missingRequiredTranslations.length > 0 && (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "white",
                                    mt: 1,
                                    display: "block",
                                  }}
                                >
                                  Missing Required Translations:
                                </Typography>
                                <Box component="ul" sx={{ m: 0, p: 0, pl: 2 }}>
                                  {missingRequiredTranslations.map((lang) => (
                                    <Typography
                                      component="li"
                                      variant="caption"
                                      key={lang}
                                      sx={{ color: "#ff8a80" }}
                                    >
                                      {lang}
                                    </Typography>
                                  ))}
                                </Box>
                              </>
                            )}
                          </Box>
                        }
                      >
                        <Box
                          component="span"
                          sx={{
                            backgroundColor: getTranslationColor(
                              phrase,
                              languagesToTranslate
                            ),
                            color: "#000000",
                            fontSize: "0.7rem",
                            py: 0.3,
                            px: 0.7,
                            borderRadius: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            ml: 1,
                          }}
                        >
                          <GTranslate
                            fontSize="inherit"
                            sx={{ mr: 0.3, fontSize: "0.9rem" }}
                          />
                          {languagesToTranslate &&
                            `${
                              getTranslationRatio(phrase, languagesToTranslate)
                                .available
                            }/${
                              getTranslationRatio(phrase, languagesToTranslate)
                                .total
                            }`}
                        </Box>
                      </Tooltip>
                    )}

                  {/* Brand/batch indicator */}
                  {displayBrandNames && (
                    <Tooltip title={brandNames?.join(", ") || ""}>
                      <Box
                        component="span"
                        sx={{
                          backgroundColor: "grey.200",
                          color: "grey.700",
                          fontSize: "0.7rem",
                          py: 0.3,
                          px: 0.6,
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                        }}
                      >
                        {displayBrandNames}
                      </Box>
                    </Tooltip>
                  )}

                  {/* Garment type match icon with tooltip */}
                  {phrase.is_matching_garment_type && (
                    <Box
                      component="span"
                      sx={{
                        backgroundColor: "success.main",
                        color: "success.contrastText",
                        fontSize: "0.7rem",
                        py: 0.3,
                        px: 0.6,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.3,
                      }}
                    >
                      {garmentType}
                    </Box>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}
